/**
 * Page controls for the past-runs table. There is no shared pagination component in this repo, and
 * this is the only paged surface, so it is deliberately small rather than general.
 *
 * The "showing" line is not decoration: on a surface where every number carries its denominator, a
 * page of ten runs has to say what it is ten of.
 *
 * ── WHY IT IS A COUNT AND TWO GLYPHS ────────────────────────────────────────
 * This used to render BELOW the table as a full bar — labelled `Previous`/`Next` buttons, a
 * `Page 2 of 3` span, and a `Showing 10 of 25 runs · 10 per page` line pushed to the right. Figma
 * `334:514` puts the control in the table's own TOOLBAR instead (node `334:530`, the `actions`
 * group), as `10 of 27 runs` plus two icon-only chevrons — which is what a ten-row page needs and
 * no more, and which stops the panel spending a whole row of vertical height on a control that is
 * inert for any sync with fewer than eleven runs.
 *
 * Two facts the old bar stated in visible text now travel differently, deliberately rather than by
 * omission:
 *   - The PAGE POSITION (`Page 2 of 3`) moves into this `<nav>`'s accessible name. The frame drops
 *     it, and with two chevrons on screen a sighted reader can see which end is blocked; a screen
 *     reader cannot, so the number has to survive somewhere and the landmark's own name is where it
 *     is announced without adding a fourth thing to the row.
 *   - The PAGE SIZE (`· 10 per page`) is gone outright. It described the control's own mechanics,
 *     not the data, and `10 of 27` already implies it.
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/_shared/components/ui/button';

import { exact } from '../lib/run-view';

interface PastRunsPaginationProps {
  /** 1-based. */
  page: number;
  pageCount: number;
  totalRuns: number;
  /** How many rows the current page actually rendered — the numerator of the "showing" line. */
  loadedRuns: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

export function PastRunsPagination({
  page,
  pageCount,
  totalRuns,
  loadedRuns,
  onPageChange,
  disabled = false,
}: PastRunsPaginationProps): React.ReactElement {
  // Clamped here rather than trusted from the caller: a disabled button is a UI state, not a
  // guarantee — keyboard activation during a re-render can still fire the handler.
  function goTo(next: number): void {
    if (next < 1 || next > pageCount || next === page) return;
    onPageChange(next);
  }

  return (
    // The page position rides on the landmark's name — see this file's header. It is `Past runs
    // pages` first so the landmark list still reads as a list of landmarks, with the position as
    // the qualifier rather than the headline.
    <nav aria-label={`Past runs pages, page ${page} of ${pageCount}`} className="flex items-center gap-2">
      {/* `exact`, not a humanized count: this is the one line that says what the ten rows above are
          ten OF, and `1.2K of 1.2K` would make a full window indistinguishable from a nearly-full
          one. */}
      <span className="text-xs text-muted-foreground">
        {exact(loadedRuns)} of {exact(totalRuns)} runs
      </span>
      <Button
        variant="secondary"
        size="sm"
        iconOnly
        aria-label="Previous page"
        disabled={disabled || page <= 1}
        Icon={<ChevronLeft aria-hidden />}
        onClick={() => {
          goTo(page - 1);
        }}
      />
      <Button
        variant="secondary"
        size="sm"
        iconOnly
        aria-label="Next page"
        disabled={disabled || page >= pageCount}
        Icon={<ChevronRight aria-hidden />}
        onClick={() => {
          goTo(page + 1);
        }}
      />
    </nav>
  );
}
